import React, { useState, useEffect, useCallback } from 'react';
import { t } from '@lingui/macro';
import moment from 'moment';
import Loader from '../Loader';
import { IMart } from '../../../interfaces/types';
import { fetchFootfallTotal } from '../../../../reducers/api/getReportData';
import {
    getAttendanceZone,
    IReportComparisonRow,
    IReportBreakdownRow,
} from '../../../helpers/reportHelpers';
import { numberWithSpaces } from '../../../helpers/index';
import ReportComparisonChart from './ReportComparisonChart';
import ReportComparisonTable from './ReportComparisonTable';
import ReportGroupedBarChart from './ReportGroupedBarChart';
import ExportButtons from './ExportButtons';

interface IProps {
    mart: IMart;
    referenceDate: moment.Moment;
    yearsBack: number;
}

const ReportMonthly = ({ mart, referenceDate, yearsBack }: IProps) => {
    const [loading, setLoading] = useState(false);
    const [totalRows, setTotalRows] = useState<IReportComparisonRow[]>([]);
    const [locationRows, setLocationRows] = useState<IReportBreakdownRow[]>([]);
    const [zoneRows, setZoneRows] = useState<IReportBreakdownRow[]>([]);
    const [weeklyRows, setWeeklyRows] = useState<IReportBreakdownRow[]>([]);
    const [yearColumns, setYearColumns] = useState<{ year: number; label: string }[]>([]);

    const monthStart = referenceDate.clone().startOf('month');
    const monthEnd = referenceDate.clone().endOf('month').startOf('day');
    const monthStartStr = monthStart.format('YYYY-MM-DD');
    const monthEndStr = monthEnd.format('YYYY-MM-DD');

    const fetchData = useCallback(async () => {
        setLoading(true);

        const attendanceZone = getAttendanceZone(mart);
        const zoneIds = attendanceZone ? [attendanceZone.id] : [];

        const cols: { year: number; label: string }[] = [];
        for (let y = 0; y <= yearsBack; y++) {
            const ms = monthStart.clone().subtract(y, 'years');
            cols.push({ year: ms.year(), label: ms.format('MM/YYYY') });
        }
        setYearColumns(cols);

        try {
            // --- Section 1: Total monthly footfall ---
            const totalPromises: Promise<number>[] = [];
            for (let y = 0; y <= yearsBack; y++) {
                const ms = monthStart.clone().subtract(y, 'years');
                const me = ms.clone().endOf('month').startOf('day');
                totalPromises.push(fetchFootfallTotal(ms.format('YYYY-MM-DD'), me.format('YYYY-MM-DD'), zoneIds, []));
            }
            const totalValues = await Promise.all(totalPromises);

            const tRows: IReportComparisonRow[] = totalValues.map((val, y) => {
                const ms = monthStart.clone().subtract(y, 'years');
                const me = ms.clone().endOf('month').startOf('day');
                return {
                    year: ms.year(),
                    label: ms.format('MM/YYYY'),
                    dateFrom: ms.format('YYYY-MM-DD'),
                    dateTo: me.format('YYYY-MM-DD'),
                    value: val
                };
            });
            setTotalRows(tRows);

            // --- Section 2: By Location ---
            const locRows: IReportBreakdownRow[] = [];
            for (const loc of mart.location) {
                const locPromises: Promise<number>[] = [];
                for (let y = 0; y <= yearsBack; y++) {
                    const ms = monthStart.clone().subtract(y, 'years');
                    const me = ms.clone().endOf('month').startOf('day');
                    locPromises.push(fetchFootfallTotal(ms.format('YYYY-MM-DD'), me.format('YYYY-MM-DD'), [], [loc.id]));
                }
                const locValues = await Promise.all(locPromises);
                const values = locValues.map((val, y) => ({
                    year: monthStart.clone().subtract(y, 'years').year(),
                    value: val
                }));
                locRows.push({ label: loc.name, values });
            }
            setLocationRows(locRows);

            // --- Section 3: By Zone ---
            const zRows: IReportBreakdownRow[] = [];
            for (const zone of mart.zone) {
                const zPromises: Promise<number>[] = [];
                for (let y = 0; y <= yearsBack; y++) {
                    const ms = monthStart.clone().subtract(y, 'years');
                    const me = ms.clone().endOf('month').startOf('day');
                    zPromises.push(fetchFootfallTotal(ms.format('YYYY-MM-DD'), me.format('YYYY-MM-DD'), [zone.id], []));
                }
                const zValues = await Promise.all(zPromises);
                const values = zValues.map((val, y) => ({
                    year: monthStart.clone().subtract(y, 'years').year(),
                    value: val
                }));
                zRows.push({ label: zone.name, values });
            }
            setZoneRows(zRows);

            // --- Section 4: Weekly breakdown within month ---
            const wRows: IReportBreakdownRow[] = [];
            let current = monthStart.clone();
            let weekNum = 1;

            while (current.isSameOrBefore(monthEnd)) {
                const weekStart = current.clone();
                let weekEnd = current.clone().add(6, 'days');
                if (weekEnd.isAfter(monthEnd)) {
                    weekEnd = monthEnd.clone();
                }

                const weekPromises: Promise<number>[] = [];
                for (let y = 0; y <= yearsBack; y++) {
                    const ws = weekStart.clone().subtract(y, 'years');
                    const we = weekEnd.clone().subtract(y, 'years');
                    weekPromises.push(fetchFootfallTotal(ws.format('YYYY-MM-DD'), we.format('YYYY-MM-DD'), zoneIds, []));
                }
                const weekValues = await Promise.all(weekPromises);
                const values = weekValues.map((val, y) => ({
                    year: monthStart.clone().subtract(y, 'years').year(),
                    value: val
                }));

                wRows.push({
                    label: t`Týždeň` + ' ' + weekNum,
                    values
                });

                current.add(7, 'days');
                weekNum++;
            }
            setWeeklyRows(wRows);
        } catch (e) {
            console.error('Error fetching monthly report data', e);
        } finally {
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mart, monthStartStr, monthEndStr, yearsBack]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Convert totalRows to breakdown format for the table
    const totalBreakdownRows: IReportBreakdownRow[] = totalRows.length > 0
        ? [{ label: t`Celkom`, values: totalRows.map(r => ({ year: r.year, value: r.value })) }]
        : [];

    const buildXlsxInput = () => ({
        martName:   mart.name,
        reportType: t`Mesačný report`,
        dateLabel:  referenceDate.format('MMMM YYYY'),
        columns:    yearColumns,
        sections: [
            ...(totalBreakdownRows.length > 0
                ? [{ title: t`Celková mesačná návštevnosť`, rows: totalBreakdownRows, layout: 'total' as const, chartId: 'monthly-total' }]
                : []),
            ...(locationRows.length > 0
                ? [{ title: t`Návštevnosť podľa vstupov`, rows: locationRows, layout: 'breakdown' as const, chartId: 'monthly-location' }]
                : []),
            ...(zoneRows.length > 0
                ? [{ title: t`Návštevnosť podľa zón`, rows: zoneRows, layout: 'breakdown' as const, chartId: 'monthly-zone' }]
                : []),
            ...(weeklyRows.length > 0
                ? [{ title: t`Týždenný prehľad v mesiaci`, rows: weeklyRows, layout: 'breakdown' as const, chartId: 'monthly-weekly' }]
                : []),
        ],
        kpis: totalRows.length > 0 ? [
            {
                label: t`Celková návštevnosť`,
                value: numberWithSpaces(totalRows[0].value),
                sub:   referenceDate.format('MMMM YYYY'),
            },
        ] : undefined,
    });

    return (
        <>
            {/* Section 1: Total monthly footfall */}
            <div className="box mgt-30 mgb-30 pd-40 pdl-25lg pdr-25lg" style={{ position: 'relative' }}>
                <div className="loader-wrapper" style={{ position: loading ? 'relative' : undefined, overflow: loading ? 'hidden' : undefined, minHeight: loading ? 120 : undefined }}>
                    <Loader value={loading} />
                </div>
                {!loading && (
                    <>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                            <ExportButtons buildInput={buildXlsxInput} />
                        </div>
                        <ReportComparisonChart
                            chartId="monthly-total"
                            title={t`Celková mesačná návštevnosť`}
                            subtitle={referenceDate.format('MMMM YYYY')}
                            rows={totalRows}
                        />
                        <ReportComparisonTable
                            title={t`Celková mesačná návštevnosť`}
                            columns={yearColumns}
                            rows={totalBreakdownRows}
                        />
                    </>
                )}
            </div>

            {/* Section 2: By Location */}
            {!loading && locationRows.length > 0 && (
                <div className="box mgt-30 mgb-30 pd-40 pdl-25lg pdr-25lg">
                    <ReportGroupedBarChart
                        chartId="monthly-location"
                        title={t`Porovnanie vstupov`}
                        columns={yearColumns}
                        rows={locationRows}
                    />
                    <ReportComparisonTable
                        title={t`Návštevnosť podľa vstupov`}
                        columns={yearColumns}
                        rows={locationRows}
                    />
                </div>
            )}

            {/* Section 3: By Zone */}
            {!loading && zoneRows.length > 0 && (
                <div className="box mgt-30 mgb-30 pd-40 pdl-25lg pdr-25lg">
                    <ReportGroupedBarChart
                        chartId="monthly-zone"
                        title={t`Porovnanie zón`}
                        columns={yearColumns}
                        rows={zoneRows}
                    />
                    <ReportComparisonTable
                        title={t`Návštevnosť podľa zón`}
                        columns={yearColumns}
                        rows={zoneRows}
                    />
                </div>
            )}

            {/* Section 4: Weekly breakdown within month */}
            {!loading && weeklyRows.length > 0 && (
                <div className="box mgt-30 mgb-30 pd-40 pdl-25lg pdr-25lg">
                    <ReportGroupedBarChart
                        chartId="monthly-weekly"
                        title={t`Týždenný prehľad – graf`}
                        columns={yearColumns}
                        rows={weeklyRows}
                    />
                    <ReportComparisonTable
                        title={t`Týždenný prehľad v mesiaci`}
                        columns={yearColumns}
                        rows={weeklyRows}
                    />
                </div>
            )}
        </>
    );
};

export default ReportMonthly;